import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Chip,
	Paper,
	CircularProgress,
	Alert,
} from '@mui/material';
import { apiService } from '../services/api';

// ── Types ────────────────────────────────────────────────────────────────────

interface PermissionsInsightProps {
	snapshotId: string;
}

interface ProfileRow {
	id: string;
	profile_name: string;
	user_license: string | null;
	is_custom: boolean;
	description: string | null;
	total_user_count: number | null;
	active_user_count: number | null;
	app_visible_count: number;
	obj_read_count: number;
	field_readable_count: number;
	custom_permissions_count: number;
	[key: string]: unknown;
}

type SortField = 'profile_name' | 'active_user_count' | 'total_user_count' | 'user_license' | 'is_custom';
type SortDir = 'asc' | 'desc';

// ── Column definitions ───────────────────────────────────────────────────────

const COLUMNS: { field: SortField; label: string; align?: 'left' | 'right'; minWidth?: number }[] = [
	{ field: 'profile_name', label: 'Profile Name', minWidth: 200 },
	{ field: 'active_user_count', label: 'Active Users', align: 'right' },
	{ field: 'total_user_count', label: 'Total Users', align: 'right' },
	{ field: 'user_license', label: 'User License' },
	{ field: 'is_custom', label: 'Custom' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function compare(a: ProfileRow, b: ProfileRow, field: SortField, dir: SortDir): number {
	let av: unknown = a[field];
	let bv: unknown = b[field];

	if (av === null || av === undefined) av = dir === 'asc' ? Infinity : -Infinity;
	if (bv === null || bv === undefined) bv = dir === 'asc' ? Infinity : -Infinity;

	if (typeof av === 'string' && typeof bv === 'string') {
		return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
	}
	if (typeof av === 'boolean' && typeof bv === 'boolean') {
		return dir === 'asc' ? (Number(av) - Number(bv)) : (Number(bv) - Number(av));
	}
	const numA = Number(av);
	const numB = Number(bv);
	return dir === 'asc' ? numA - numB : numB - numA;
}

function formatCount(value: number | null | undefined): string {
	if (value === null || value === undefined) return '\u2014';
	return value.toLocaleString();
}

// ── Main component ───────────────────────────────────────────────────────────

export const PermissionsInsight: React.FC<PermissionsInsightProps> = ({ snapshotId }) => {
	const [profiles, setProfiles] = useState<ProfileRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [sortField, setSortField] = useState<SortField>('active_user_count');
	const [sortDir, setSortDir] = useState<SortDir>('desc');

	const loadProfiles = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const runsResponse = await apiService.getAnalysisRuns(snapshotId);
			if (!runsResponse.success || !runsResponse.data) {
				setError(runsResponse.error || 'Failed to load analysis runs');
				return;
			}

			const completedRuns = runsResponse.data.runs
				.filter((r) => r.status === 'completed' || r.status === 'completed_with_errors')
				.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());

			if (completedRuns.length === 0) {
				setError('No completed analysis runs found. Run an analysis first from the Analysis page.');
				return;
			}

			const resultsResponse = await apiService.getAnalysisResults(completedRuns[0].run_id, 'profiles');
			if (!resultsResponse.success || !resultsResponse.data) {
				setError(resultsResponse.error || 'Failed to load profile results');
				return;
			}

			setProfiles(resultsResponse.data.results as ProfileRow[]);
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setLoading(false);
		}
	}, [snapshotId]);

	useEffect(() => {
		loadProfiles();
	}, [loadProfiles]);

	const sorted = useMemo(
		() => [...profiles].sort((a, b) => compare(a, b, sortField, sortDir)),
		[profiles, sortField, sortDir],
	);

	const handleSort = (field: SortField) => {
		if (field === sortField) {
			setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortField(field);
			setSortDir(field === 'profile_name' || field === 'user_license' ? 'asc' : 'desc');
		}
	};

	// Summary stats
	const totalProfiles = profiles.length;
	const customProfiles = profiles.filter((p) => p.is_custom).length;
	const profilesWithNoActiveUsers = profiles.filter(
		(p) => p.active_user_count !== null && p.active_user_count === 0,
	).length;
	const hasUserData = profiles.some((p) => p.active_user_count !== null);

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
	}

	if (profiles.length === 0) {
		return (
			<Alert severity="info" sx={{ m: 2 }}>
				No profile data found in this analysis run.
			</Alert>
		);
	}

	return (
		<Box>
			<Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
				Permissions Overview
			</Typography>

			{/* Summary chips */}
			<Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
				<Chip label={`${totalProfiles} Profiles`} variant="outlined" />
				<Chip label={`${customProfiles} Custom`} variant="outlined" color="primary" />
				{hasUserData && profilesWithNoActiveUsers > 0 && (
					<Chip
						label={`${profilesWithNoActiveUsers} with no active users`}
						variant="outlined"
						color="warning"
					/>
				)}
			</Box>

			{!hasUserData && (
				<Alert severity="info" sx={{ mb: 2 }}>
					User assignment data is not available for this analysis run. Connect a Salesforce org with live access to populate active/total user counts.
				</Alert>
			)}

			{/* Profile table */}
			<Paper variant="outlined">
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								{COLUMNS.map((col) => (
									<TableCell
										key={col.field}
										align={col.align || 'left'}
										sx={{ fontWeight: 600, minWidth: col.minWidth }}
									>
										<TableSortLabel
											active={sortField === col.field}
											direction={sortField === col.field ? sortDir : 'asc'}
											onClick={() => handleSort(col.field)}
										>
											{col.label}
										</TableSortLabel>
									</TableCell>
								))}
								<TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{sorted.map((row) => (
								<TableRow key={row.id} hover>
									<TableCell>
										<Typography variant="body2" sx={{ fontWeight: 500 }}>
											{row.profile_name}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography
											variant="body2"
											sx={{
												fontWeight: 600,
												color: row.active_user_count != null && row.active_user_count > 0
													? 'success.main'
													: row.active_user_count === 0
														? 'text.secondary'
														: undefined,
											}}
										>
											{formatCount(row.active_user_count)}
										</Typography>
									</TableCell>
									<TableCell align="right">
										{formatCount(row.total_user_count)}
									</TableCell>
									<TableCell>
										{row.user_license || '\u2014'}
									</TableCell>
									<TableCell>
										{row.is_custom ? (
											<Chip label="Custom" size="small" color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />
										) : (
											<Chip label="Standard" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />
										)}
									</TableCell>
									<TableCell>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{
												display: '-webkit-box',
												WebkitLineClamp: 2,
												WebkitBoxOrient: 'vertical',
												overflow: 'hidden',
												maxWidth: 300,
											}}
										>
											{row.description || '\u2014'}
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</Box>
	);
};
